import React from 'react';
import {
    Bookmark, Heart, FolderPlus, Users, Globe, Link2, ChevronRight,
    MoreVertical, Edit2, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SidebarProfileCard } from '../../components/SidebarProfileCard';
import { getCategoryIcon } from './utils';

// ─── CategorySidebar ───────────────────────────────────────────────
// The left rail on desktop and the sheet content on mobile. Renders,
// in order:
//   1. SidebarProfileCard    — username / plan / public link
//   2. "Collections" header  — with + button to open the create modal
//   3. All Bookmarks         — pseudo-scope, count = totalCount
//   4. Favorites             — pseudo-scope, filters by `is_favorite`
//   5. Owned categories      — one row per parent, with subcategories
//   6. "Shared with me"      — separator + owner-scoped rows
//
// `isMobile` controls two things:
//   • whether the whole thing gets left/right padding (mobile sheet)
//   • whether tapping a category also closes the mobile menu sheet
//
// All state + handlers arrive as props — the component is presentational.
export function CategorySidebar({
    isMobile = false,
    // user / plan
    user,
    hasPublicItems,
    // selection state
    selectedCategory,
    selectedSubcategory,
    showFavorites,
    // aggregate data
    ownedParentCategories,
    sharedWithMeCategories,
    categories,
    totalCount,
    // handlers — selection
    selectCategory,
    setSelectedCategory,
    setSelectedSubcategory,
    setShowFavorites,
    // handlers — modals + mutations
    setMobileMenuOpen,
    resetCategoryForm,
    setCategoryModalOpen,
    openEditCategory,
    setShareCategoryTarget,
    setShareCategoryOpen,
    setCategoryParentId,
    setDeleteCategoryId,
}) {
    return (
        <div className={isMobile ? 'px-2' : ''}>
            <SidebarProfileCard
                user={user}
                hasPublicItems={hasPublicItems}
                onClick={() => { if (isMobile) setMobileMenuOpen(false); }}
            />
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-base">Collections</h2>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { resetCategoryForm(); setCategoryModalOpen(true); if (isMobile) setMobileMenuOpen(false); }}
                    className="h-8 w-8 p-0"
                    data-testid="create-category-btn"
                >
                    <FolderPlus className="w-4 h-4" />
                </Button>
            </div>

            <div className="space-y-1">
                <button
                    onClick={() => { selectCategory(null); setShowFavorites(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        !selectedCategory && !showFavorites ? 'bg-primary text-primary-foreground shadow-md' : 'hover:bg-secondary'
                    }`}
                    data-testid="all-bookmarks-btn"
                >
                    <Bookmark className="w-4 h-4" />
                    <span className="flex-1 font-medium text-sm">All Bookmarks</span>
                    <span className="text-xs opacity-70">{totalCount}</span>
                </button>

                <button
                    onClick={() => { selectCategory(null); setShowFavorites(true); if (isMobile) setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        showFavorites ? 'bg-red-50 text-red-600 shadow-sm ring-1 ring-red-100' : 'hover:bg-secondary'
                    }`}
                    data-testid="favorites-btn"
                >
                    <Heart className={`w-4 h-4 ${showFavorites ? 'fill-red-500' : ''}`} />
                    <span className="flex-1 font-medium text-sm">Favorites</span>
                </button>

                {ownedParentCategories.map((category) => {
                    const IconComponent = getCategoryIcon(category.icon);
                    const isSelected = selectedCategory === category.category_id;
                    const subcats = categories.filter((c) => c.parent_id === category.category_id);
                    return (
                        <div key={category.category_id}>
                            <div className="group relative">
                                <button
                                    onClick={() => selectCategory(category.category_id)}
                                    className={`w-full flex items-center gap-2 px-3 py-2.5 pr-8 rounded-xl text-left transition-colors ${
                                        isSelected
                                            ? 'bg-primary text-primary-foreground shadow-md'
                                            : 'hover:bg-secondary'
                                    }`}
                                    data-testid={`category-${category.category_id}`}
                                    title={category.name}
                                >
                                    {(category.share_visibility === 'public' || category.share_visibility === 'unlisted') && (
                                        <span className="w-3.5 shrink-0 flex items-center justify-center">
                                            {category.share_visibility === 'public' && (
                                                <Globe
                                                    className={`w-3.5 h-3.5 ${isSelected ? 'text-white/80' : 'text-emerald-600'}`}
                                                    aria-label="Public collection"
                                                    data-testid={`category-public-mark-${category.category_id}`}
                                                />
                                            )}
                                            {category.share_visibility === 'unlisted' && (
                                                <Link2
                                                    className={`w-3.5 h-3.5 ${isSelected ? 'text-white/80' : 'text-sky-600'}`}
                                                    aria-label="Unlisted collection"
                                                    data-testid={`category-unlisted-mark-${category.category_id}`}
                                                />
                                            )}
                                        </span>
                                    )}
                                    <div
                                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${isSelected ? 'bg-white/20' : ''}`}
                                        style={{ backgroundColor: isSelected ? undefined : `${category.color}15` }}
                                    >
                                        <IconComponent className="w-4 h-4" style={{ color: isSelected ? 'white' : category.color }} />
                                    </div>
                                    <span className="flex-1 font-medium text-sm flex items-center gap-1.5 min-w-0">
                                        <span className="truncate min-w-0">{category.name}</span>
                                    </span>
                                    {category.is_shared && (
                                        <span className={`flex items-center gap-0.5 text-[10px] ${isSelected ? 'text-white/70' : 'text-muted-foreground'}`} title={`Shared${category.my_role ? ` (${category.my_role})` : ''} - ${category.member_count || 0} members`}>
                                            <Users className="w-3 h-3" />{category.member_count || ''}
                                        </span>
                                    )}
                                    {category.is_shared && category.new_since_last_view > 0 && !isSelected && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground leading-none" data-testid={`new-badge-${category.category_id}`}>
                                            {category.new_since_last_view} new
                                        </span>
                                    )}
                                    {category.bookmark_count > 0 && (
                                        <span className="text-xs opacity-70">{category.bookmark_count}</span>
                                    )}
                                </button>

                                {!category.my_role && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-muted-foreground"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => openEditCategory(category)}>
                                                <Edit2 className="w-4 h-4 mr-2" /> Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => { setShareCategoryTarget(category); setShareCategoryOpen(true); }}>
                                                <Users className="w-4 h-4 mr-2" /> Share
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => { setCategoryParentId(category.category_id); resetCategoryForm(); setCategoryParentId(category.category_id); setCategoryModalOpen(true); }}>
                                                <FolderPlus className="w-4 h-4 mr-2" /> Add sub-collection
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => setDeleteCategoryId(category.category_id)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                                {category.my_role && (
                                    <div className="absolute right-1 top-1/2 -translate-y-1/2">
                                        <span className="text-[10px] text-muted-foreground">{category.owner_name}</span>
                                    </div>
                                )}
                            </div>
                            {/* Subcategories */}
                            {subcats.length > 0 && (
                                <div className="ml-6 mt-0.5 space-y-0.5">
                                    {subcats.map((sub) => {
                                        const isSubSel = selectedSubcategory === sub.category_id;
                                        return (
                                            <div key={sub.category_id} className="group relative">
                                                <button
                                                    onClick={() => { setSelectedCategory(category.category_id); setSelectedSubcategory(isSubSel ? null : sub.category_id); if (isMobile) setMobileMenuOpen(false); }}
                                                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 pr-8 rounded-lg text-left text-sm transition-colors ${
                                                        isSubSel ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-secondary/60'
                                                    }`}
                                                >
                                                    <span className="w-3.5 shrink-0 flex items-center justify-center">
                                                        {sub.share_visibility === 'public' && (
                                                            <Globe
                                                                className="w-3.5 h-3.5 text-emerald-600"
                                                                aria-label="Public collection"
                                                                data-testid={`category-public-mark-${sub.category_id}`}
                                                            />
                                                        )}
                                                        {sub.share_visibility === 'unlisted' && (
                                                            <Link2
                                                                className="w-3.5 h-3.5 text-sky-600"
                                                                aria-label="Unlisted collection"
                                                                data-testid={`category-unlisted-mark-${sub.category_id}`}
                                                            />
                                                        )}
                                                    </span>
                                                    <ChevronRight className="w-3 h-3" />
                                                    <span className="flex-1 truncate flex items-center gap-1.5 min-w-0">
                                                        <span className="truncate min-w-0">{sub.name}</span>
                                                    </span>
                                                    {sub.is_shared && (
                                                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                                            <Users className="w-2.5 h-2.5" />{sub.member_count || ''}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] opacity-60">{sub.bookmark_count}</span>
                                                </button>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className={`absolute right-0.5 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-muted-foreground ${isMobile ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <MoreVertical className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => openEditCategory(sub)}>
                                                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => { setShareCategoryTarget(sub); setShareCategoryOpen(true); }}>
                                                            <Users className="w-4 h-4 mr-2" /> Share
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => setDeleteCategoryId(sub.category_id)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Shared with me section */}
                {sharedWithMeCategories.length > 0 && (
                    <>
                        <div className="flex items-center gap-2 px-3 pt-4 pb-1">
                            <div className="h-px flex-1 bg-border" />
                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="shared-with-me-divider">Shared with me</span>
                            <div className="h-px flex-1 bg-border" />
                        </div>
                        {sharedWithMeCategories.map((category) => {
                            const IconComponent = getCategoryIcon(category.icon);
                            const isSelected = selectedCategory === category.category_id;
                            return (
                                <div key={category.category_id}>
                                    <div className="group relative">
                                        <button
                                            onClick={() => selectCategory(category.category_id)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 pr-9 rounded-xl text-left transition-colors ${
                                                isSelected
                                                    ? 'bg-primary text-primary-foreground shadow-md'
                                                    : 'hover:bg-secondary'
                                            }`}
                                            data-testid={`category-${category.category_id}`}
                                            title={`${category.name} • by ${category.owner_name}${category.member_count ? ` • ${category.member_count} members` : ''}`}
                                        >
                                            <div
                                                className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${isSelected ? 'bg-white/20' : ''}`}
                                                style={{ backgroundColor: isSelected ? undefined : `${category.color}15` }}
                                            >
                                                <IconComponent className="w-4 h-4" style={{ color: isSelected ? 'white' : category.color }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="font-medium text-sm truncate block">{category.name}</span>
                                                <span className={`text-[10px] truncate block ${isSelected ? 'text-white/60' : 'text-muted-foreground'}`}>by {category.owner_name}</span>
                                            </div>
                                            {category.new_since_last_view > 0 && !isSelected ? (
                                                <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground leading-none" data-testid={`new-badge-${category.category_id}`}>
                                                    {category.new_since_last_view}
                                                </span>
                                            ) : (
                                                <span className="shrink-0 text-xs opacity-70">{category.bookmark_count}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}
